<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import LaisConfirmModal from 'dashboard/components-next/LaisConfirmModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import PropertyOffersAPI from 'dashboard/api/propertyOffers';

const props = defineProps({
  integration: { type: Object, required: true },
  propertyOfferId: { type: [Number, String], default: null },
  canManage: { type: Boolean, default: false },
});

const emit = defineEmits(['updated']);
const { t } = useI18n();
const notify = useAlert();
const cancelOpen = ref(false);
const cancelReason = ref('');
const action = ref('');
const MIN_CANCEL_REASON_LENGTH = 10;
const MAX_CANCEL_REASON_LENGTH = 200;

const actionable = computed(() =>
  ['pending', 'sent', 'partially_signed'].includes(props.integration.status)
);
const canAct = computed(() =>
  Boolean(props.propertyOfferId && props.canManage)
);
const canSync = computed(() => canAct.value && actionable.value);
const canCancel = computed(() => canAct.value && actionable.value);
const cancelReasonLength = computed(() => cancelReason.value.trim().length);
const isCancelReasonValid = computed(
  () =>
    cancelReasonLength.value >= MIN_CANCEL_REASON_LENGTH &&
    cancelReasonLength.value <= MAX_CANCEL_REASON_LENGTH
);

const messageFrom = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;

const sync = async () => {
  if (!canSync.value) return;
  action.value = 'sync';
  try {
    await PropertyOffersAPI.syncContract(props.propertyOfferId);
    notify(t('SALES.DETAIL.RECEIPT.SYNC_SUCCESS'));
    emit('updated', 'sync');
  } catch (error) {
    notify(messageFrom(error, t('SALES.DETAIL.RECEIPT.ERROR_SYNC')));
  } finally {
    action.value = '';
  }
};

const openCancel = () => {
  if (canCancel.value) cancelOpen.value = true;
};

const closeCancel = () => {
  if (!action.value) cancelOpen.value = false;
};

const cancel = async () => {
  const reason = cancelReason.value.trim();
  if (!isCancelReasonValid.value) {
    notify(t('SALES.DETAIL.RECEIPT.CANCEL_REASON_ERROR'));
    return;
  }

  action.value = 'cancel';
  try {
    await PropertyOffersAPI.cancelContract(props.propertyOfferId, { reason });
    notify(t('SALES.DETAIL.RECEIPT.CANCEL_SUCCESS'));
    cancelOpen.value = false;
    cancelReason.value = '';
    emit('updated', 'cancel');
  } catch (error) {
    notify(messageFrom(error, t('SALES.DETAIL.RECEIPT.ERROR_CANCEL')));
  } finally {
    action.value = '';
  }
};
</script>

<template>
  <div>
    <div v-if="canSync || canCancel" class="flex flex-wrap gap-2">
      <LaisPrimaryButton
        v-if="canSync"
        size="sm"
        variant="ghost"
        icon="i-lucide-refresh-cw"
        :is-loading="action === 'sync'"
        :disabled="Boolean(action)"
        @click="sync"
      >
        {{ t('SALES.DETAIL.RECEIPT.SYNC') }}
      </LaisPrimaryButton>
      <LaisPrimaryButton
        v-if="canCancel"
        size="sm"
        variant="ghost"
        icon="i-lucide-ban"
        :is-loading="action === 'cancel'"
        :disabled="Boolean(action)"
        @click="openCancel"
      >
        {{ t('SALES.DETAIL.RECEIPT.CANCEL_ENVELOPE') }}
      </LaisPrimaryButton>
    </div>

    <LaisConfirmModal
      :show="cancelOpen"
      :title="t('SALES.DETAIL.RECEIPT.CANCEL_TITLE')"
      :description="t('SALES.DETAIL.RECEIPT.CANCEL_DESCRIPTION')"
      :confirm-text="t('SALES.DETAIL.RECEIPT.CANCEL_CONFIRM')"
      :cancel-text="t('SALES.ACTIONS.CLOSE')"
      icon="i-lucide-ban"
      :is-loading="action === 'cancel'"
      :confirm-disabled="!isCancelReasonValid"
      @close="closeCancel"
      @confirm="cancel"
    >
      <label class="mt-4 block text-sm font-semibold text-lais-text-body">
        {{ t('SALES.DETAIL.RECEIPT.CANCEL_REASON') }}
        <textarea
          v-model="cancelReason"
          rows="3"
          :maxlength="MAX_CANCEL_REASON_LENGTH"
          class="mt-1 w-full rounded-lais border border-lais-border px-3 py-2 text-sm"
          :placeholder="t('SALES.DETAIL.RECEIPT.CANCEL_REASON_PLACEHOLDER')"
        />
      </label>
      <div class="mt-3 grid gap-2 text-sm text-amber-800">
        <p>{{ t('SALES.DETAIL.RECEIPT.CANCEL_CLIENT_NOTICE') }}</p>
        <p>{{ t('SALES.DETAIL.RECEIPT.CANCEL_LENGTH_NOTICE') }}</p>
      </div>
    </LaisConfirmModal>
  </div>
</template>
